<template>
  <div class="admin-shell">
    <!-- Overlay (solo mobile) -->
    <transition name="overlay-fade">
      <div v-if="mobileOpen && isMobile" class="overlay" @click="mobileOpen = false" />
    </transition>

    <!-- Sidebar -->
    <AppSidebar
      :is-collapsed="isCollapsed"
      :mobile-open="mobileOpen"
      :screen-width="screenWidth"
      :nav-groups="navGroups"
      :admin-nombre="adminNombre"
      :admin-rol="adminRol"
      @toggle-collapse="isCollapsed = !isCollapsed"
      @close-mobile="mobileOpen = false"
      @logout="handleLogout"
    />

    <!-- Main content -->
    <div class="main-wrapper" :class="{ 'is-collapsed': isCollapsed && !isMobile }">
      <!-- Topbar (solo mobile) -->
      <header v-if="isMobile" class="topbar">
        <button
          class="menu-btn"
          @click="mobileOpen = !mobileOpen"
          aria-label="Abrir menú"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div class="topbar-title">{{ currentTitle }}</div>
      </header>

      <!-- Content -->
      <main class="content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAdminStore } from "@/store/adminStore";
import AppSidebar from "@/components/DashboardEvento/AppSidebar.vue";

const route = useRoute();
const router = useRouter();
const store = useAdminStore();

const isCollapsed = ref(false);
const mobileOpen = ref(false);
const screenWidth = ref(window.innerWidth);

// ── Computed ──────────────────────────────────────────────────────────────
const isMobile = computed(() => screenWidth.value <= 1024);
const adminNombre = computed(() => store.admin?.nombre || "Admin");
const adminRol = computed(() => "Administrador");

// ── Navegación ────────────────────────────────────────────────────────────
// Grupo activo: gestión del evento (lo que ya existía en DashboardLayout.vue)
const navEvento = [
  { to: "/dashboard", icon: "bi-grid-fill", label: "Resumen" },
  { to: "/dashboard/evento", icon: "bi-calendar-event-fill", label: "Evento" },
  { to: "/dashboard/peleadores", icon: "bi-people-fill", label: "Peleadores" },
  {
    to: "/dashboard/asistentes",
    icon: "bi-person-badge-fill",
    label: "Asistentes",
  },
  {
    to: "/dashboard/inscripciones",
    icon: "bi-clipboard2-check-fill",
    label: "Inscripciones",
  },
  { to: "/dashboard/boletos", icon: "bi-ticket-perforated-fill", label: "Boletos" },
  {
    to: "/dashboard/patrocinadores",
    icon: "bi-ticket-perforated-fill",
    label: "Patrocinadores",
  },
];

const navGroups = computed(() => [{ label: "Gestión de evento", items: navEvento }]);

const titleMap = {
  "/dashboard": "Resumen",
  "/dashboard/evento": "Evento",
  "/dashboard/peleadores": "Peleadores",
  "/dashboard/asistentes": "Asistentes",
  "/dashboard/inscripciones": "Inscripciones",
  "/dashboard/boletos": "Boletos",
  "/dashboard/patrocinadores": "Patrocinadores",
};
const currentTitle = computed(() => titleMap[route.path] ?? "Dashboard");

// ── Methods ───────────────────────────────────────────────────────────────
async function handleLogout() {
  store.logout();
  router.push("/dashboard/login");
}

function updateScreenWidth() {
  screenWidth.value = window.innerWidth;
  if (screenWidth.value > 1024 && mobileOpen.value) {
    mobileOpen.value = false;
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────
onMounted(() => {
  window.addEventListener("resize", updateScreenWidth);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreenWidth);
});
</script>

<style scoped>
.admin-shell {
  display: flex;
  min-height: 100vh;
  background: #0f1520;
  color: #ffffff;
  font-family: "Inter", "Segoe UI", sans-serif;
}

/* Overlay */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.62);
  backdrop-filter: blur(3px);
  z-index: 45;
}
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Main wrapper */
.main-wrapper {
  flex: 1;
  margin-left: 240px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 0;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.main-wrapper.is-collapsed {
  margin-left: 72px;
}

/* Topbar (mobile only) */
.topbar {
  display: none;
  align-items: center;
  gap: 12px;
  height: 64px;
  padding: 0 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(6, 13, 26, 0.92);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 30;
}
.topbar-title {
  font-size: 15px;
  font-weight: 800;
  color: #ffffff;
}
.menu-btn {
  border: none;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  transition: background 0.2s ease;
}
.menu-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}
.menu-btn svg {
  width: 20px;
  height: 20px;
}

/* Content */
.content {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow-x: hidden;
}

/* Mobile */
@media (max-width: 1024px) {
  .main-wrapper {
    margin-left: 0 !important;
  }
  .topbar {
    display: flex;
  }
}
</style>
